import Drawer from '@frontend/components/drawer/Drawer'
import { Bag } from '@frontend/components/icons/outline'
import { CHECKOUT_URL } from '@frontend/libs/constants'
import useCart from '@frontend/libs/hooks/useCart'
import { useCartStore } from '@frontend/libs/stores/useCartStore'
import { formatPrice } from '@frontend/libs/utils/format-price'
import { Button } from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import { useEffect } from 'react'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'

import CartItem from './components/CartItem'

export const EmptyCart = ({ onClose }: { onClose: () => void }) => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <p className="return-to-shop text-center">
      <Bag className="mx-auto mb-4 block h-20 w-20" />
      <span className="mb-16 block uppercase">
        {__('Your Shopping Bag is Empty.', 'asavagroup')}
      </span>
      <Button color="primary" onClick={onClose} type="button" variant="solid">
        {__('Return to shop', 'asavagroup')}
      </Button>
    </p>
  </div>
)

function MiniCart() {
  const { data, error } = useCart().get()
  const { closeMiniCart, isMiniCartOpen, items, setCart, totals } =
    useCartStore((state) => state)

  if (error) {
    toast.error(error?.message || 'Failed to fetch cart data')
  }

  useEffect(() => {
    if (data) {
      setCart(data)
    }
  }, [data, setCart])

  return (
    <Drawer isOpen={isMiniCartOpen} onClose={closeMiniCart} position="right">
      <Drawer.Header>
        <h5 className="mb-0 text-xl font-medium uppercase leading-normal">
          {__('Shopping Bag', 'asavagroup')}
        </h5>
      </Drawer.Header>
      <Drawer.Content>
        {items.length === 0 && <EmptyCart onClose={closeMiniCart} />}
        {items && items.length > 0 && (
          <div className="h-[calc(100dvh_-_24rem)] overflow-y-auto md:h-[calc(100dvh_-_20rem)]">
            <div className="divide-y divide-gray-200">
              {items.map((item) => (
                <CartItem item={item} key={item.key} />
              ))}
            </div>
          </div>
        )}
      </Drawer.Content>
      {items && items.length > 0 && (
        <Drawer.Footer>
          <p className="flex items-center justify-between font-medium uppercase">
            <span>{__('Subtotal:', 'asavagroup')}</span>
            {totals && <span>{formatPrice(totals.totalItems)}</span>}
          </p>
          <p className="mt-0.5 text-xs uppercase text-gray-500">
            {__('Shipping and taxes calculated at checkout.', 'asavagroup')}
          </p>
          <div className="mb-2 mt-10 grid grid-cols-1 gap-2 md:grid-cols-2">
            <Button
              className="w-full"
              color="primary"
              onClick={closeMiniCart}
              type="button"
              variant="bordered"
            >
              {__('Continue Shopping', 'asavagroup')}
            </Button>
            <Button
              as="a"
              className="w-full uppercase"
              color="primary"
              href={CHECKOUT_URL}
              variant="solid"
            >
              {__('Checkout', 'asavagroup')}
            </Button>
          </div>
        </Drawer.Footer>
      )}
    </Drawer>
  )
}

export default MiniCart
